import type { AppRouterOutputs } from '@alison/api/router';

export const SIGNAL_TABS = ['new', 'high', 'active', 'dismissed'] as const;
export const SIGNAL_GROUPINGS = ['none', 'workflow', 'account'] as const;

export type SignalsFeed = AppRouterOutputs['signals']['feed'];
export type Signal = SignalsFeed['signals'][number];
export type SignalTab = (typeof SIGNAL_TABS)[number];
export type SignalGrouping = (typeof SIGNAL_GROUPINGS)[number];

export interface SignalReadState {
  read: ReadonlySet<string>;
  dismissed: ReadonlySet<string>;
}

export interface SignalCounts {
  new: number;
  high: number;
  active: number;
  dismissed: number;
}

export interface SignalGroup {
  key: string;
  label: string | null;
  signals: Signal[];
}

/**
 * - `caught-up`: everything in reach was read and nothing is active.
 * - `incomplete`: nothing active is listed, but some results could not be read or the feed
 *   was cut off, so "all caught up" would be a claim the data does not support.
 */
export type FeedCompleteness = 'has-active' | 'caught-up' | 'incomplete';

function isHighSeverity(signal: Signal): boolean {
  return signal.severity === 'high';
}

export function signalCounts(signals: Signal[], state: SignalReadState): SignalCounts {
  const active = signals.filter((signal) => !state.dismissed.has(signal.runId));
  return {
    new: active.filter((signal) => !state.read.has(signal.runId)).length,
    high: active.filter(isHighSeverity).length,
    active: active.length,
    dismissed: signals.length - active.length,
  };
}

export function signalsIn(tab: SignalTab, signals: Signal[], state: SignalReadState): Signal[] {
  if (tab === 'dismissed') return signals.filter((signal) => state.dismissed.has(signal.runId));
  const active = signals.filter((signal) => !state.dismissed.has(signal.runId));
  if (tab === 'high') return active.filter(isHighSeverity);
  return tab === 'new' ? active.filter((signal) => !state.read.has(signal.runId)) : active;
}

export function completenessOf(
  feed: Pick<SignalsFeed, 'unreadable' | 'truncated'>,
  activeCount: number,
): FeedCompleteness {
  if (activeCount > 0) return 'has-active';
  return feed.unreadable > 0 || feed.truncated ? 'incomplete' : 'caught-up';
}

function groupLabelOf(signal: Signal, grouping: SignalGrouping): string | null {
  if (grouping === 'workflow') return signal.workflow;
  if (grouping === 'account') return signal.accountNames.join(', ') || null;
  return null;
}

export function groupSignals(signals: Signal[], grouping: SignalGrouping): SignalGroup[] {
  if (grouping === 'none') return [{ key: 'all', label: null, signals }];
  const groups = new Map<string, SignalGroup>();
  for (const signal of signals) {
    const label = groupLabelOf(signal, grouping);
    const key = label ?? '';
    const group = groups.get(key) ?? { key, label, signals: [] };
    group.signals.push(signal);
    groups.set(key, group);
  }
  return [...groups.values()];
}
